import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Cpu, Zap, ArrowRight, Flame, AlertTriangle, ExternalLink } from "lucide-react";

const URGENCY = {
  low: { color: "#22ff77", label: "Aktivan", icon: Cpu, anim: "" },
  medium: { color: "#22ff77", label: "U toku", icon: Zap, anim: "" },
  high: { color: "#ff7a18", label: "Pri kraju", icon: Flame, anim: "animate-pulse" },
  critical: { color: "#ff3b3b", label: "Skoro gotovo!", icon: AlertTriangle, anim: "animate-pulse" },
};

export default function CycleProgress({ compact = false }) {
  const [cycle, setCycle] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [c, cfg] = await Promise.all([
          api.get("/cycles/current"),
          api.get("/payments/crypto/config"),
        ]);
        if (!mounted) return;
        setCycle(c.data);
        setConfig(cfg.data);
      } catch (e) {
        void e;
      }
      if (mounted) setLoading(false);
    };
    load();
    const t = setInterval(load, 15000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  if (loading) {
    return (
      <div className="nasa-card p-6 text-zinc-500 text-xs uppercase tracking-widest" data-testid="cycle-progress-loading">
        Učitavanje ciklusa...
      </div>
    );
  }
  if (!cycle) return null;

  const u = URGENCY[cycle.urgency] || URGENCY.low;
  const Icon = u.icon;
  const isUrgent = cycle.urgency === "critical" || cycle.urgency === "high";
  const wallet = config?.wallet_address;
  const isPlaceholder = !wallet || config?.placeholder_address;
  const tronscanUrl = !isPlaceholder ? `https://tronscan.org/#/address/${wallet}` : null;

  return (
    <div
      className={`nasa-card p-6 md:p-7 relative overflow-hidden ${isUrgent ? "border-[#ff7a18]/50" : ""}`}
      data-testid="cycle-progress"
      data-urgency={cycle.urgency}
      style={isUrgent ? { boxShadow: `0 0 0 1px ${u.color}40, 0 0 28px ${u.color}25` } : {}}
    >
      <div className="absolute inset-0 nasa-card-bg-laptop" /><div className="absolute inset-0 nasa-grid-bg opacity-40" />
      <div className="relative">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-11 h-11 rounded-sm flex items-center justify-center ${u.anim}`}
              style={{ background: `${u.color}1a`, border: `1px solid ${u.color}55` }}
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} style={{ color: u.color }} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500">
                Aktivni Giveaway ciklus · <span style={{ color: u.color }} data-testid="cycle-urgency-label">{u.label}</span>
              </div>
              <div className="font-display text-xl font-bold leading-tight">
                Mašina <span className="text-neon-orange" data-testid="cycle-machine-number">#{cycle.machine_number}</span>{" "}
                <span className="text-zinc-600 text-sm font-normal">· Adria Region</span>
              </div>
            </div>
          </div>
          {!compact && (
            <Link
              to="/ciklusi"
              data-testid="cycle-history-link"
              className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-400 hover:text-[#ff7a18]"
            >
              Istorija <ArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>

        {/* FOMO Countdown banner */}
        {cycle.tickets_to_close > 0 && (
          <div
            data-testid="fomo-banner"
            className={`mb-4 border rounded-sm p-4 ${isUrgent ? "bg-[#ff7a18]/8" : "bg-[#22ff77]/5"} flex items-center justify-between flex-wrap gap-3`}
            style={{ borderColor: `${u.color}55` }}
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: u.color }}>
                Još do mašine #{cycle.machine_number}
              </div>
              <div className="font-display text-3xl md:text-4xl font-bold mt-1" data-testid="cycle-tickets-to-close" style={{ color: u.color }}>
                {cycle.tickets_to_close} <span className="text-base text-zinc-500 font-normal">tiketa</span>
                <span className="text-zinc-600 mx-2">·</span>
                {cycle.remaining_eur.toLocaleString("sr-Latn-RS")} €
              </div>
            </div>
            {isUrgent && (
              <Link
                to="/kupi-tikete"
                data-testid="fomo-buy-cta"
                className="bg-[#ff7a18] text-black font-bold px-5 py-2.5 uppercase tracking-widest text-xs rounded-sm hover:brightness-110 glow-orange whitespace-nowrap"
              >
                Kupi tiket sada
              </Link>
            )}
          </div>
        )}

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="border border-white/10 bg-black/40 p-3 rounded-sm group">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Sakupljeno</div>
            {tronscanUrl ? (
              <a
                href={tronscanUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="cycle-collected-tronscan-link"
                title="Otvori wallet na Tronscan exploreru"
                className="font-display text-2xl font-bold text-[#22ff77] mt-1 inline-flex items-center gap-1.5 hover:underline decoration-[#22ff77]/40 underline-offset-4"
              >
                <span data-testid="cycle-collected">{cycle.collected_eur.toLocaleString("sr-Latn-RS")} €</span>
                <ExternalLink className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100" />
              </a>
            ) : (
              <div className="font-display text-2xl font-bold text-[#22ff77] mt-1" data-testid="cycle-collected">
                {cycle.collected_eur.toLocaleString("sr-Latn-RS")} €
              </div>
            )}
            <div className="text-[9px] text-zinc-600 mt-1 leading-tight">
              {tronscanUrl ? "klikni za Tronscan · samo realne kupovine" : "samo realne kupovine · bez bonus tokena"}
            </div>
          </div>
          <div className="border border-white/10 bg-black/40 p-3 rounded-sm">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Cilj</div>
            <div className="font-display text-2xl font-bold text-[#ff7a18] mt-1">
              {cycle.target_eur.toLocaleString("sr-Latn-RS")} €
            </div>
          </div>
          <div className="border border-white/10 bg-black/40 p-3 rounded-sm">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Tiketi</div>
            <div className="font-display text-2xl font-bold mt-1" data-testid="cycle-tickets-sold">
              {cycle.tickets_sold}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-[10px] uppercase tracking-widest">
            <span className="text-zinc-500">Napredak ka mašini</span>
            <span style={{ color: u.color }} className="font-bold" data-testid="cycle-progress-pct">{cycle.progress_pct}%</span>
          </div>
          {tronscanUrl ? (
            <a
              href={tronscanUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="cycle-progress-bar-link"
              title="Proveri stanje na Tronscan exploreru"
              className="block h-3 bg-black border border-white/10 rounded-sm overflow-hidden relative cursor-pointer hover:border-[#22ff77]/40"
            >
              <div
                className={`h-full transition-all ${isUrgent ? "animate-pulse" : ""}`}
                style={{
                  width: `${cycle.progress_pct}%`,
                  background:
                    cycle.urgency === "critical"
                      ? "linear-gradient(90deg, #ff7a18 0%, #ff3b3b 100%)"
                      : "linear-gradient(90deg, #22ff77 0%, #ff7a18 100%)",
                  boxShadow: `0 0 14px ${u.color}80`,
                }}
              />
            </a>
          ) : (
            <div className="h-3 bg-black border border-white/10 rounded-sm overflow-hidden relative">
              <div
                className={`h-full transition-all ${isUrgent ? "animate-pulse" : ""}`}
                style={{
                  width: `${cycle.progress_pct}%`,
                  background:
                    cycle.urgency === "critical"
                      ? "linear-gradient(90deg, #ff7a18 0%, #ff3b3b 100%)"
                      : "linear-gradient(90deg, #22ff77 0%, #ff7a18 100%)",
                  boxShadow: `0 0 14px ${u.color}80`,
                }}
              />
            </div>
          )}
          <p className="text-[10px] text-[#ff7a18] leading-relaxed mt-1.5 flex items-start gap-1.5 bg-black/70 px-3 py-2 rounded-sm border border-[#ff7a18]/20" data-testid="cycle-usdt-disclaimer">
            <span className="text-[#22ff77]">●</span>
            <span>
              Prikaz u Evrima zbog lakšeg razumevanja. <span className="text-[#ff7a18]">Sva sredstva se prikupljaju i obračunavaju u USDT-u na TRC20 mreži. 1 EUR ≈ 1 USDT.</span>
              {tronscanUrl && (
                <>
                  {" "}
                  <a href={tronscanUrl} target="_blank" rel="noreferrer" className="text-[#22ff77] hover:underline inline-flex items-center gap-0.5">
                    Proveri uživo na blockchain-u <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                </>
              )}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
